import { useRefreshTokenMutation, useRefreshMobileTokenMutation } from 'store/services/core/login';
import { useLazyStartImpersonationQuery, useLazyStopImpersonationQuery, useLazyStartSubstitutionQuery } from 'store/services/core/user';
import { jwtDecode } from 'jwt-decode';
import { createContext, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { useStorage, useStore } from '.';
import { setAuth } from 'store/slices';
import type { RootState } from 'store';
import { LoadingContainer } from 'components/common/LoadingContainer';
import type { STORAGE_VALUES } from 'types/storage';
import { STORAGE_KEYS } from 'types/storage';
import type { STORE_VALUES } from 'types/store';
import { STORE_KEYS } from 'types/store';

import { coreApi } from 'store/services/core';
import { flowApi } from 'store/services/flow';
import { ReactNativeMessageType, type AuthState } from 'types';
import { invokeRNMessage, isMobileApp } from 'utils/common';
import { parseHeliosError } from 'utils/helios/parseHeliosError';
import { createPostLoginRedirect } from 'utils/postLoginRedirect';

type JWTToken = {
  user_id: string;
  email: string;
  full_name: string;
  'http://schemas.microsoft.com/ws/2008/06/identity/claims/role': string[];
  tenant_id: string;
  exp: number;
  iss: string;
  aud: string;
};

type LoginContextType = {
  handleLogin: (jwtToken: AuthState, stayLoggedIn: boolean, loginViaStore?: boolean, isImpersonation?: boolean) => void;
  handleLogout: () => void;
  handleImpersonate: (id: string) => void;
  handleUnimpersonate: () => void;
  handleSubstitute: (id: string) => void;
  handleCancelSubstitution: () => void;
  user: JWTToken | null;
  permissions?: {
    userAdmin: boolean;
    templateAdmin: boolean;
    settingAdmin: boolean;
  };
  isAdmin?: boolean;
  isImpersonating: boolean;
  isSubstituting: boolean;
};

const LoginContext = createContext<LoginContextType>({
  handleLogin: () => {},
  handleLogout: () => {},
  handleImpersonate: () => {},
  handleUnimpersonate: () => {},
  handleSubstitute: () => {},
  handleCancelSubstitution: () => {},
  user: null,
  isImpersonating: false,
  isSubstituting: false,
});

const adminRoles = ['UserAdmin', 'TemplateAdmin', 'SettingAdmin'];

const LoginProvider = memo(({ children }: { children: React.ReactNode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const mobileApp = isMobileApp();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [shouldBeLogin, setShouldBeLogin] = useState(true);

  const { getItem: getStoreItem, setItem: setStoreItem, clearItem: clearStoreItem, clearEntireStore } = useStore();
  const { getItem: getStorageItem, setItem: setStorageItem, clearItem: clearStorageItem } = useStorage();

  const [refreshTokenLoginTrigger] = useRefreshTokenMutation();
  const [refreshMobileToken] = useRefreshMobileTokenMutation();

  const jwtToken = useSelector((state: RootState) => state.auth.jwtToken);

  const stateParam = new URL(window.location.href).searchParams.get('state');

  let state: {
    reason: string;
    errorCode: number;
    currentLocation: string;
  } | null = null;

  if (stateParam) {
    try {
      const decodedStateParam = decodeURIComponent(stateParam);
      state = JSON.parse(decodedStateParam);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to parse state parameter:', error);
    }
  }

  const [user, setUser] = useState<JWTToken | null>(null);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [isSubstituting, setIsSubstituting] = useState(false);

  const [triggerStopImpersonation] = useLazyStopImpersonationQuery();
  const [triggerStartImpersonation] = useLazyStartImpersonationQuery();
  const [triggerSubstitute] = useLazyStartSubstitutionQuery();

  const [permissions, setPermissions] = useState<{
    userAdmin: boolean;
    templateAdmin: boolean;
    settingAdmin: boolean;
  }>({
    userAdmin: false,
    templateAdmin: false,
    settingAdmin: false,
  });

  const checkLogin = (): [boolean, STORE_VALUES['auth'] | null] => {
    const auth = getStoreItem(STORE_KEYS.AUTH);

    return [!!auth, auth];
  };

  const checkRefreshToken = (): [boolean, STORAGE_VALUES['refreshToken'] | null] => {
    const refreshToken = getStorageItem(STORAGE_KEYS.REFRESH_TOKEN);

    return [!!refreshToken, refreshToken];
  };

  const savePostLoginRedirect = (url: string) => {
    const redirect = createPostLoginRedirect(url, window.location.origin);

    if (redirect) {
      setStoreItem(STORE_KEYS.POST_LOGIN_REDIRECT, redirect);
      return;
    }

    try {
      const parsedUrl = new URL(url);

      if (parsedUrl.origin === window.location.origin && parsedUrl.pathname === '/') {
        clearStoreItem(STORE_KEYS.POST_LOGIN_REDIRECT);
      }
    } catch {
      return;
    }
  };

  const handleLogin = (auth: AuthState, stayLoggedIn: boolean, isLoggedViaStore?: boolean, isImpersonation?: boolean, isSubstitution?: boolean) => {
    if (!auth) return;

    setIsLoggingIn(true);

    const decodedToken: JWTToken = jwtDecode(auth.jwtToken ?? '');

    setUser(decodedToken);

    setPermissions({
      userAdmin: decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'].includes('UserAdmin'),
      templateAdmin: decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'].includes('TemplateAdmin'),
      settingAdmin: decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'].includes('SettingAdmin'),
    });

    if (isImpersonation || isSubstitution) {
      const [isLogin, oldAuth] = checkLogin();

      if (isLogin && oldAuth) {
        oldAuth.jwtToken = auth.jwtToken;

        dispatch(setAuth(oldAuth));
        setStoreItem(STORE_KEYS.AUTH, oldAuth);
      }
    } else {
      dispatch(setAuth(auth));
      setStoreItem(STORE_KEYS.AUTH, auth);
    }

    if (stayLoggedIn && auth.refreshToken && auth.tokenProvider)
      setStorageItem(STORAGE_KEYS.REFRESH_TOKEN, {
        refreshToken: auth.refreshToken,
        provider: auth.tokenProvider,
      });

    setIsLoggingIn(false);
    setShouldBeLogin(false);

    const userRoles = decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
    const isImpersonateUserAdmin = adminRoles.some((role) => userRoles.includes(role));

    if (isLoggedViaStore || window.location.pathname === '/login') {
      navigate(isImpersonateUserAdmin ? '/admin' : '/user', { replace: true });
    }
  };

  const loginViaRefreshToken = async () => {
    const refreshToken = getStorageItem(STORAGE_KEYS.REFRESH_TOKEN);
    const storedAuth = getStoreItem(STORE_KEYS.AUTH);
    const isImpersonate = getStoreItem(STORE_KEYS.IS_IMPERSONATING);
    const isSubstitute = getStoreItem(STORE_KEYS.IS_SUBSTITUTING);

    if (isImpersonate) {
      setStoreItem(STORE_KEYS.IS_IMPERSONATING, false);
      setIsImpersonating(false);
    }

    if (isSubstitute) {
      setStoreItem(STORE_KEYS.IS_SUBSTITUTING, false);
      setIsSubstituting(false);
    }

    if (!refreshToken) {
      setShouldBeLogin(false);
      savePostLoginRedirect(window.location.href);
      navigate('/login');
      return;
    }

    dispatch(
      setAuth({
        refreshToken: refreshToken.refreshToken,
        tokenProvider: refreshToken.provider,
        jwtToken: storedAuth?.jwtToken ?? '',
        apiKey: storedAuth?.apiKey ?? process.env.API_KEY ?? '',
      })
    );

    const response = await (mobileApp && refreshToken.provider === 'Google' ? refreshMobileToken(null) : refreshTokenLoginTrigger(null));

    if (!response || 'error' in response) {
      clearStorageItem(STORAGE_KEYS.REFRESH_TOKEN);
      savePostLoginRedirect(window.location.href);
      navigate('/login');
      return;
    }

    if (response.data) {
      if (mobileApp && refreshToken.provider === 'Microsoft') {
        invokeRNMessage(ReactNativeMessageType.MicrosoftRefreshToken, {
          refreshToken: response.data.refreshToken,
        });
      }

      if (mobileApp && refreshToken.provider === 'Google') {
        const newRefreshToken = response.data.refreshToken;

        if (newRefreshToken?.trim()) {
          invokeRNMessage(ReactNativeMessageType.GoogleRefreshToken, {
            refreshToken: newRefreshToken,
          });
        }
      }

      handleLogin(
        {
          jwtToken: response.data.jwtToken,
          refreshToken: response.data.refreshToken,
          tokenProvider: refreshToken.provider,
          apiKey: storedAuth?.apiKey ?? process.env.API_KEY ?? '',
        },
        true,
        window.location.pathname === '/login'
      );
    }
  };

  const handleLoginViaStore = async () => {
    if (window.location.pathname === '/microsoftoauth2') {
      setShouldBeLogin(false);
      return;
    }
    if (window.location.pathname === '/googleoauth2') {
      setShouldBeLogin(false);
      return;
    }

    if (window.location.pathname === '/') {
      clearStoreItem(STORE_KEYS.POST_LOGIN_REDIRECT);
    }

    if (state && state.errorCode === 401) {
      const refreshToken = getStorageItem(STORAGE_KEYS.REFRESH_TOKEN);

      if (!refreshToken) {
        setShouldBeLogin(false);
        savePostLoginRedirect(state.currentLocation);
        navigate('/login');
        return;
      }

      await loginViaRefreshToken();

      const url = new URL(state.currentLocation);

      navigate(`${url.pathname}${url.search}${url.hash}`);

      setShouldBeLogin(false);
      return;
    }

    const [isLogin, auth] = checkLogin();

    if (isLogin && auth) {
      handleLogin(auth, true, window.location.pathname === '/login');
      return;
    }

    const [isRefreshToken, refreshToken] = checkRefreshToken();

    if (isRefreshToken && refreshToken) {
      loginViaRefreshToken();

      return;
    }

    setShouldBeLogin(false);
    savePostLoginRedirect(window.location.href);
    navigate('/login');
  };

  const handleLogout = () => {
    if (mobileApp) {
      invokeRNMessage(ReactNativeMessageType.Logout, { jwtToken });
      return;
    }
    setUser(null);
    clearEntireStore();
    clearStorageItem(STORAGE_KEYS.REFRESH_TOKEN);
    navigate('/login');
  };

  const handleImpersonate = async (id: string) => {
    try {
      const data = await triggerStartImpersonation({ id }).unwrap();
      const impersonationJwtToken = data.jwtToken;

      handleLogin({ jwtToken: impersonationJwtToken }, false, false, true);
      setStoreItem(STORE_KEYS.IS_IMPERSONATING, true);

      dispatch(coreApi.util.resetApiState());
      dispatch(flowApi.util.resetApiState());

      const decodedToken: JWTToken = jwtDecode(impersonationJwtToken);
      const userRoles = decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];
      const isImpersonateUserAdmin = adminRoles.some((role) => userRoles.includes(role));

      setIsImpersonating(true);
      if (!isImpersonateUserAdmin) navigate('/user');
    } catch (error) {
      toast.error(parseHeliosError(error) || t('core.users.start_impersonation_error'));
    }
  };

  const handleUnimpersonate = async () => {
    try {
      const data = await triggerStopImpersonation().unwrap();
      const restoredJwtToken = data.jwtToken;

      setStoreItem(STORE_KEYS.IS_IMPERSONATING, false);
      setIsImpersonating(false);

      handleLogin({ jwtToken: restoredJwtToken }, false, false, true);

      navigate(0); // This is a hack to force a refresh of the page
    } catch (error) {
      toast.error(parseHeliosError(error) || t('core.users.stop_impersonation_error'));
    }
  };

  const handleSubstitute = (id: string) => {
    triggerSubstitute({ substitutionId: id })
      .unwrap()
      .then((data) => {
        handleLogin({ jwtToken: data.jwtToken }, false, false, false, true);
        setStoreItem(STORE_KEYS.IS_SUBSTITUTING, true);

        dispatch(coreApi.util.resetApiState());
        dispatch(flowApi.util.resetApiState());

        setIsSubstituting(true);
        navigate('/user', { replace: true });
      });
  };

  const handleCancelSubstitution = () => {
    triggerStopImpersonation()
      .unwrap()
      .then((data) => {
        handleLogin({ jwtToken: data.jwtToken }, false, false, false, true);
        setStoreItem(STORE_KEYS.IS_SUBSTITUTING, false);
        setIsSubstituting(false);
        navigate(0); // This is a hack to force a refresh of the page
      });
  };

  useEffect(() => {
    setIsImpersonating(getStoreItem(STORE_KEYS.IS_IMPERSONATING) ?? false);
    setIsSubstituting(getStoreItem(STORE_KEYS.IS_SUBSTITUTING) ?? false);
    handleLoginViaStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <LoginContext.Provider
      value={{
        handleLogin,
        handleLogout,
        handleUnimpersonate,
        handleImpersonate,
        handleSubstitute,
        handleCancelSubstitution,
        user,
        permissions,
        isAdmin: permissions?.settingAdmin || permissions?.templateAdmin || permissions?.userAdmin,
        isImpersonating,
        isSubstituting,
      }}>
      <LoadingContainer
        loading={isLoggingIn || shouldBeLogin}
        styles={{
          width: '100vw',
          height: '100vh',
        }}>
        {children}
      </LoadingContainer>
    </LoginContext.Provider>
  );
});

export { LoginContext, LoginProvider };
